import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type { Sd849OrgSettingsPatch } from '@erp/shared-types';

/** The organization's 849 settings (SD-86); an absent key is unset. */
export function useSd849OrgSettings() {
  return useQuery(orpc.shipAndDebit.sd849OrgSettings.get.queryOptions());
}

/**
 * Change SOME of the organization's 849 settings. Send only the keys being
 * changed (see `diffSd849OrgSettings`); the server merges them into the stored
 * rows, so nothing else is rewritten. The response is the stored state, written
 * straight into the cache; the query is still refetched, because two saves in
 * flight can answer out of order and leave the older snapshot in the cache.
 */
export function useUpdateSd849OrgSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Sd849OrgSettingsPatch) =>
      rpcClient.shipAndDebit.sd849OrgSettings.update({ patch }),
    onSuccess: (stored) => {
      queryClient.setQueryData(
        orpc.shipAndDebit.sd849OrgSettings.get.queryKey(),
        stored,
      );
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.sd849OrgSettings.get.queryKey(),
      });
    },
  });
}
